import React, { ForwardRefRenderFunction, forwardRef, useMemo, useState } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { InvoiceDetailsFormContainerProps } from "models/propTypes/invoicePropTypes";
import { DimensionEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { InvoiceFormTypes, UtilFormTypes } from "models/formTypes";
import { TodoConstants, FeatureConstants } from "utils/constants";
//#endregion
//#region components
import InvoiceDetailsForm from "./detailsForm";
import { useDimensionMaps, useNavigationHistory } from "components/common";
import { useFeatureService, useInvoiceService, useRecurringInvoiceService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";

const InvoiceDetailsFormContainer: ForwardRefRenderFunction<FormHandles, InvoiceDetailsFormContainerProps> = (
  props,
  ref
) => {
  const recurringInvoiceService = useRecurringInvoiceService();
  const invoiceService = useInvoiceService();
  const featureService = useFeatureService();

  const { invoice } = props;
  const [currency] = useState<string>(invoice.currency ?? "");
  const [dimensionAmount] = useState<number>(+(invoice.totalAmount - invoice.totalTax));
  const [isAutomaticRemindersFeatureEnabled, setIsAutomaticRemindersFeatureEnabled] = React.useState(false);

  const isReadOnly = true;

  const { dimensions, forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.Invoice);

  const { getRouteStateValue, removeRouteStateValue } = useNavigationHistory();

  const initialValues = useMemo((): InvoiceFormTypes.Details => {
    const allowedDimensionIds = dimensions.flatMap((d) => d.value).map((d) => d.id);

    const invoiceForm: InvoiceFormTypes.Details = {
      serialNumber: (invoice as WintSuperkollAPIModel.Invoice)?.serialNumber,
      customerName: invoice.customerName,
      customerOrgNr: invoice.customerOrgNo,
      referencePerson: invoice.customerReference?.referenceName,
      contactPerson: invoice.contactPerson,
      owner: invoice.ownerName,
      amountIncludingVat: invoice.totalAmount,
      amountExcludingVat: invoice.totalAmount - invoice.totalTax,
      vat: invoice.totalTax,
      currency: invoice.currency,
      paymentTerms: invoice.paymentTerms,
      paymentMethod: invoice.invoicePaymentMethod
        ? LocalizationHelperInstance.getFormattedMessage(`Invoice.PaymentMethods_${invoice.invoicePaymentMethod}`)
        : "",
      bg: invoice.invoicePaymentBgNumber,
      dimensions: invoiceService
        .getInvoiceDimensionData(dimensionAmount, invoice.dimensions)
        ?.filter((d) => allowedDimensionIds.includes(d.dimensionId)),
      periodisationSummary: invoice.rows?.map((row) => {
        const r = row as WintSuperkollAPIModel.InvoiceRow;
        const hasDateFields = r.periodisationStartDate !== undefined && r.periodisationStartDate !== null;
        return {
          text: row.overriddenDescription ?? row.description,
          mode: (hasDateFields ? "date" : "month") as "date" | "month",
          periodisation: row.periodisation
            ? hasDateFields
              ? {
                  start: r.periodisationStartDate
                    ? DateHelper.parseDateOnly(r.periodisationStartDate as unknown as string)
                    : undefined,
                  end: r.periodisationEndDate
                    ? DateHelper.parseDateOnly(r.periodisationEndDate as unknown as string)
                    : undefined,
                }
              : {
                  start: DateHelper.getDateFromYearMonth(
                    (r.periodisationStartYear ?? 0) * 100 + (r.periodisationStartMonth ?? 0)
                  ),
                  end: DateHelper.getDateFromYearMonth(
                    (r.periodisationEndYear ?? 0) * 100 + (r.periodisationEndMonth ?? 0)
                  ),
                }
            : undefined,
        };
      }),
    };

    if (props.isRecurring) {
      const recurringInvoice = invoice as WintSuperkollAPIModel.RecurringInvoice;
      invoiceForm.recurringDescription = recurringInvoice.recurringDescription;
      invoiceForm.recurringInvoiceStartDate = recurringInvoice.firstPostingDate;
      invoiceForm.recurringInvoiceFrequencyName = recurringInvoiceService.getFrequencyText(
        recurringInvoice.invoiceFrequency
      );
      invoiceForm.recurringInvoiceLastInvoice = recurringInvoice.untilFurtherNotice
        ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.RepeatUntilNotice)
        : recurringInvoice.stopDate
          ? LocalizationHelperInstance.getFormattedDate(recurringInvoice.stopDate)
          : "";
      invoiceForm.recurringDeliveryAfterDays = recurringInvoice.daysToDelivery
        ? `${recurringInvoice.daysToDelivery} ${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.DaysAfterInvoice
          )}`
        : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.OnInvoiceDate);
    } else {
      const regularInvoice = invoice as WintSuperkollAPIModel.Invoice;

      const hasRowLevelDimensions = regularInvoice.rows?.some(
        (row) => row.dimensions && row.dimensions.filter((d) => allowedDimensionIds.includes(d)).length > 0
      );

      if (hasRowLevelDimensions) {
        invoiceForm.hasRowLevelDimensions = true;
        invoiceForm.dimensions = invoiceService
          .getRowDimensionData(invoiceService.getProducts(regularInvoice))
          ?.filter((d) => allowedDimensionIds.includes(d.dimensionId));
      }

      invoiceForm.ocr = regularInvoice.ocrText;
      invoiceForm.paymentStatus =
        regularInvoice.paymentState === WintSuperkollAPIModel.InvoicePaymentState.Paid
          ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Paid)
          : regularInvoice.paymentState === WintSuperkollAPIModel.InvoicePaymentState.PartiallyPaid ||
              regularInvoice.paymentState === WintSuperkollAPIModel.InvoicePaymentState.PartiallyPaidOverdue
            ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.PartiallyPaid)
            : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Unpaid);
      invoiceForm.leftToPay =
        regularInvoice.paymentState !== WintSuperkollAPIModel.InvoicePaymentState.Paid
          ? regularInvoice.leftToPay
          : undefined;
      invoiceForm.dueDate = regularInvoice.dueDate;
      invoiceForm.invoiceDate = regularInvoice.postingDate;
      invoiceForm.deliveryDate = regularInvoice.deliveryDate;
      invoiceForm.paymentDate =
        (regularInvoice.payments &&
          [...regularInvoice.payments].map((i) => i.date).sort()[regularInvoice.payments.length - 1]) ??
        regularInvoice.paymentDate;
      invoiceForm.isAutomaticRemindersEnabled = regularInvoice.isAutomaticRemindersEnabled;
      invoiceForm.automaticReminderPeriod =
        regularInvoice.automaticReminderPeriod != null
          ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NumOfDays, "", {
              num: `${regularInvoice.automaticReminderPeriod}`,
            })
          : undefined;
      invoiceForm.automaticReminderDelay = regularInvoice.automaticReminderDelay
        ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NumOfDays, "", {
            num: `${regularInvoice.automaticReminderDelay}`,
          })
        : undefined;
      invoiceForm.automaticReminderAmountLimit =
        regularInvoice.automaticReminderAmountLimit != null
          ? LocalizationHelperInstance.getFormattedCurrency(
              regularInvoice.automaticReminderAmountLimit,
              regularInvoice.currency
            )
          : undefined;
      invoiceForm.automaticReminderCountLimit = regularInvoice.automaticReminderCountLimit;
      invoiceForm.automaticReminderSendMethod =
        regularInvoice.automaticReminderSendMethod != null
          ? LocalizationHelperInstance.getTranslatedEnumValue(
              TranslationKeys.Invoice.AutomaticReminderSendType,
              regularInvoice.automaticReminderSendMethod
            )
          : undefined;
      invoiceForm.automaticReminderMailToAddresses = regularInvoice.automaticReminderMailToAddresses;
    }

    return invoiceForm;
  }, [invoice, dimensions]);

  React.useEffect(() => {
    const state = getRouteStateValue(TodoConstants.RouteTodoSelectionKey);
    if (state) removeRouteStateValue(TodoConstants.RouteTodoSelectionKey);
  }, []);

  React.useEffect(() => {
    featureService
      .hasFeature(FeatureConstants.AutomaticReminders)
      .then((res) => setIsAutomaticRemindersFeatureEnabled(res));
  }, []);

  const validationSchema = useMemo(
    () =>
      Yup.object<InvoiceFormTypes.Details>({
        customerName: Yup.string(),
        customerOrgNr: Yup.string(),
        referencePerson: Yup.string(),
        contactPerson: Yup.string(),
        amountIncludingVat: Yup.number(),
        amountExcludingVat: Yup.number(),
        vat: Yup.number(),
        currency: Yup.string(),
        dueDate: Yup.date(),
        invoiceDate: Yup.date(),
        deliveryDate: Yup.date(),
        paymentDate: Yup.date(),
        paymentTerms: Yup.number(),
        paymentStatus: Yup.string(),
        paymentMethod: Yup.string(),
        bg: Yup.string(),
        ocr: Yup.string(),
        dimensions: Yup.array()
          .of(
            Yup.object<WintSuperkollAPIModel.DimensionData>({
              dimensionId: Yup.string(),
              amount: Yup.number(),
            })
          )
          .checkForcedDimensions(forcedDimensionMap)
          .validateDimensionAmounts(dimensions, dimensionAmount),
        periodisationSummary: Yup.array().of(
          Yup.object<InvoiceFormTypes.ProductPeriodisation>({
            text: Yup.string(),
            periodisation: Yup.object<UtilFormTypes.DateRange>({
              start: Yup.date(),
              end: Yup.date(),
            }),
          })
        ),
      }),
    [dimensions, forcedDimensionMap, dimensionAmount]
  );

  const handleSubmit = async (values: InvoiceFormTypes.Details) => {
    await invoiceService.updateDimensions(props.invoice.id, invoiceService.getInvoiceDimensions(values), true);
    return invoice.id;
  };

  return (
    <InvoiceDetailsForm
      ref={ref}
      validationSchema={validationSchema}
      defaultValues={initialValues}
      invoice={invoice}
      currency={currency}
      dimensionAmount={dimensionAmount}
      handleSubmit={handleSubmit}
      isReadOnly={isReadOnly}
      isRecurring={props.isRecurring ?? false}
      isAutomaticRemindersFeatureEnabled={isAutomaticRemindersFeatureEnabled}
      onFormValidationChange={props.onFormValidationChange}
      attachments={props.attachments}
      loadInvoice={props.loadInvoice}
      fileViewer={props.fileViewer}
    />
  );
};

export default forwardRef(InvoiceDetailsFormContainer);
